/**
 * Executes an array of async tasks with a maximum concurrency limit.
 * @param tasks Array of async functions to execute
 * @param limit Maximum number of concurrent tasks (default: 3)
 * @param onTaskStart Optional callback called when a task starts: (index: number) => void
 * @param onTaskComplete Optional callback called when a task completes: (index: number, result: T) => void
 * @returns Promise resolving to array of results in original order
 */
export async function executeWithConcurrencyLimit<T>(
  tasks: Array<() => Promise<T>>,
  limit: number = 3,
  onTaskStart?: (index: number) => void,
  onTaskComplete?: (index: number, result: T) => void,
): Promise<T[]> {
  const results: T[] = [];
  const executing: Promise<void>[] = [];
  let taskIndex = 0;

  for (let i = 0; i < Math.min(limit, tasks.length); i++) {
    const promise = execute();
    executing.push(promise);
  }

  await Promise.all(executing);
  return results;

  async function execute(): Promise<void> {
    while (taskIndex < tasks.length) {
      const currentIndex = taskIndex;
      taskIndex++;
      // Call callback when task starts
      if (onTaskStart) {
        onTaskStart(currentIndex);
      }
      results[currentIndex] = await tasks[currentIndex]();
      // Call callback when task completes
      if (onTaskComplete) {
        onTaskComplete(currentIndex, results[currentIndex]);
      }
    }
  }
}
